import { useApolloClient, useMutation, useQuery } from "@apollo/client/react"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import React, { useState } from "react"
import { graphql } from "@/api"
import {
  type ShoppingItemCreateMutationVariables,
  type ShoppingItemDeleteMutationVariables,
  type ShoppingItemsQuery,
  type ShoppingItemUpdateMutationVariables,
} from "@/api/graphql"
import Form from "@/google_shopping/components/form"
import Sync from "@/google_shopping/components/sync"
import SpinnerIcon from "@/images/layout/spinner.gif"
import { Settings } from "@/skylticons"
import "@/google_shopping/styles/index.css"

export const shoppingItemsDoc = graphql(`
  query ShoppingItems($id: ID!) {
    shoppingItems(id: $id) {
      id
      brandId
      tagTitle
      title
      description
      image
      imageUrl
      productType
    }
  }
`)

const shoppingItemCreateDoc = graphql(`
  mutation ShoppingItemCreate(
    $title: String!
    $description: String!
    $exampleId: String!
    $image: Upload
    $productType: String!
  ) {
    shoppingItemCreate(
      title: $title
      description: $description
      exampleId: $exampleId
      image: $image
      productType: $productType
    ) {
      shoppingItem {
        id
        brandId
        tagTitle
        title
        description
        image
        imageUrl
        productType
      }

      errors {
        message
      }
    }
  }
`)

const shoppingItemUpdateDoc = graphql(`
  mutation ShoppingItemUpdate(
    $id: ID!
    $title: String!
    $description: String!
    $image: Upload
    $deleteImage: Boolean
    $productType: String!
  ) {
    shoppingItemUpdate(
      id: $id
      title: $title
      description: $description
      image: $image
      deleteImage: $deleteImage
      productType: $productType
    ) {
      shoppingItem {
        id
        brandId
        tagTitle
        title
        description
        image
        imageUrl
        productType
      }

      errors {
        message
      }
    }
  }
`)

const shoppingItemDeleteDoc = graphql(`
  mutation ShoppingItemDelete($id: ID!) {
    shoppingItemDelete(id: $id) {
      success
      id
    }
  }
`)

interface ShoppingProps {
  exampleId: string
  exampleableId: string
  price: string
  priceVat: string
}

const Shopping: React.FC<ShoppingProps> = ({ exampleId, exampleableId, price, priceVat }) => {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery(shoppingItemsDoc, {
    variables: { id: exampleId },
  })
  const [createMutation, { loading: createLoading }] = useMutation(shoppingItemCreateDoc)
  const [updateMutation, { loading: updateLoading }] = useMutation(shoppingItemUpdateDoc)
  const [deleteMutation, { loading: deleteLoading }] = useMutation(shoppingItemDeleteDoc)

  const handleCreate = (variables: ShoppingItemCreateMutationVariables) => {
    createMutation({
      variables,
      context: { hasUpload: true },
      update: (store, { data: queryData }) => {
        if (queryData?.shoppingItemCreate?.shoppingItem) {
          const data = store.readQuery({
            query: shoppingItemsDoc,
            variables: { id: exampleId },
          })

          if (data) {
            const shoppingItems = data.shoppingItems.filter(item => !item.id.startsWith("new"))
            shoppingItems.push(queryData.shoppingItemCreate.shoppingItem)

            store.writeQuery({
              query: shoppingItemsDoc,
              variables: { id: exampleId },
              data: { ...data, shoppingItems },
            })
          }

          closeForm()
        }
      },
    })
  }
  const handleUpdate = (variables: ShoppingItemUpdateMutationVariables) => {
    updateMutation({
      variables,
      context: { hasUpload: true },
      onCompleted: data => {
        if (data.shoppingItemUpdate?.shoppingItem) {
          closeForm()
        }
      },
    })
  }
  const handleDelete = (variables: ShoppingItemDeleteMutationVariables) => {
    deleteMutation({
      variables,
      update: (store, { data: queryData }) => {
        if (queryData?.shoppingItemDelete?.success) {
          const data = store.readQuery({
            query: shoppingItemsDoc,
            variables: { id: exampleId },
          })

          if (data) {
            const shoppingItems = data.shoppingItems.filter(item => item.id !== queryData.shoppingItemDelete?.id)

            activatePrevious(() => {
              store.writeQuery({
                query: shoppingItemsDoc,
                variables: { id: exampleId },
                data: { ...data, shoppingItems },
              })
            })
          }
        }
      },
    })
  }

  const isLoading = queryLoading || createLoading || updateLoading || deleteLoading
  const client = useApolloClient()

  const closeForm = (e?: MouseEvent) => {
    if (!e || !e.target || !(e.target as Element).closest(".shoppingbox")) {
      setOpen(false)
    }
  }

  const activatePrevious = (callback?: () => void) => {
    setActiveIndex(prevIndex => {
      const newIndex = prevIndex - 1
      if (callback) callback()
      return newIndex
    })
  }

  const activateNew = () => {
    if (data) {
      const item: ShoppingItemsQuery["shoppingItems"][0] = {
        __typename: "ShoppingItem",
        id: `new-${exampleId}`,
        brandId: 0,
        title: "",
        description: "",
        tagTitle: "-",
        image: false,
        imageUrl: null,
        productType: "Business & Industrial > Signage",
      }

      client.writeQuery({
        query: shoppingItemsDoc,
        variables: { id: exampleId },
        data: { ...data, shoppingItems: [...data.shoppingItems, item] },
      })

      setActiveIndex(data.shoppingItems.length)
    }
  }

  const clearNew = () => {
    if (data) {
      const shoppingItems = data.shoppingItems.filter(item => !item.id.startsWith("new"))

      client.writeQuery({
        query: shoppingItemsDoc,
        variables: { id: exampleId },
        data: { ...data, shoppingItems },
      })

      setActiveIndex(data.shoppingItems.length - 1)
    }
  }

  const tabBar = () => {
    const items = data?.shoppingItems || []
    if (!items) {
      return null
    }

    const hideNew = items.length > 0 && items[items.length - 1].id.startsWith("new")

    return (
      <div className="tabbar">
        {items.map((item, index) => (
          <a
            key={index}
            className={cn("tab", "datatab", { active: index === activeIndex })}
            onClick={() => setActiveIndex(index)}
          >
            NR. {index + 1}
          </a>
        ))}
        {!hideNew && (
          <a className="tab newtab" onClick={activateNew}>
            +
          </a>
        )}
      </div>
    )
  }

  const tabForm = () => {
    const items = data?.shoppingItems || []
    if (!items || !items[activeIndex]) {
      return null
    }

    const active = items[activeIndex]

    if (!active) {
      return null
    }

    return (
      <Form
        key={activeIndex}
        exampleId={exampleId}
        id={active.id}
        tagTitle={active.tagTitle}
        brandId={active.brandId}
        title={active.title}
        description={active.description}
        price={price}
        image={active.image}
        imageUrl={active.imageUrl || ""}
        productType={active.productType}
        priceVat={priceVat}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onDeleteNew={clearNew}
      />
    )
  }

  const disabled = useSSRDisabled()

  if (queryError) return null

  return (
    <div
      id={`shoppingbox-${exampleId}`}
      className="shoppingbox absolute inset-y-0 right-0 flex items-center border-l pr-[5px] [&_>*]:ml-[5px]"
    >
      <Sync exampleId={exampleId} exampleableId={exampleableId} openForm={() => setOpen(true)} />
      {disabled ? null : (
        <button className="databtn hover:cursor-pointer hover:text-[#888]" onClick={() => setOpen(!open)} type="button">
          <Settings className="h-6 w-5" width={14} />
        </button>
      )}
      {open && (
        <div className="shoppingdata left-[60px] z-20 h-[530px]">
          {isLoading ? (
            <div className="loading">
              <img src={SpinnerIcon} className="spinner" />
            </div>
          ) : (
            <>
              {tabBar()}
              {tabForm()}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Shopping
